import { html } from 'lit';

import { AdVendor } from '@client/core/AdVendor.js';
import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import {
  $retailMediaAtom,
  type AspectFormat
} from '@client/core/atoms/retailMedia.js';
import { extractRetailMediaParams } from '@client/core/utils/retailMediaUtils.js';
import { triggerRequest } from '@client/retailMedia/index.js';
import {
  AdServer,
  AdSlot,
  DisplayPlacementId,
  type LayoutType,
  type PlacementId,
  PlacementStatus
} from '@schibsted-nmp/advertising-shared';

export type DisplaySettings = {
  layoutType?: 'grid' | 'list';
  aspectFormat?: AspectFormat;
};
// Track which placements have been initialized (module-level)
const initializedPlacements = new Set<PlacementId>();
const lastProcessedAtom = new Map<PlacementId, string>();

export class RetailMediaAdVendor extends AdVendor {
  declare placementId: PlacementId;

  /**
   * Display settings for layout and aspect format, sent from the search page
   */

  displaySettings?: DisplaySettings;

  constructor(
    placementId: string,
    layoutType?: LayoutType,
    aspectFormat?: AspectFormat
  ) {
    super(AdServer.Retailmedia, placementId as DisplayPlacementId);
    // Construct DisplaySettings from individual params (only if values exist)
    if (layoutType || aspectFormat) {
      this.displaySettings = {};
      if (layoutType) this.displaySettings.layoutType = layoutType;
      if (aspectFormat) this.displaySettings.aspectFormat = aspectFormat;
    }
  }

  override updated() {
    // do nothing
  }

  private unsubscribe: (() => void) | null = null;

  override cleanupEvents() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  override setupEvents() {
    const { placementId } = this;
    const isFirstInstanceForPlacement = !initializedPlacements.has(placementId);

    this.unsubscribe = $retailMediaAtom.subscribe((atom) => {
      // Skip 'init' events for subsequent instances (e.g., after layout changes causing remount)
      // We only want to trigger the initial request once per placement
      if (atom.eventType === 'init' && !isFirstInstanceForPlacement) {
        return;
      }

      if (atom.eventType === 'layout_change') {
        // we don't want to re-fetch when layout changes, only re-render
        return;
      }

      const atomKey = JSON.stringify({
        searchParams: atom.searchParams, // check on searchParams to trigger new requests on search changes
        eventType: atom.eventType
      });

      if (lastProcessedAtom.get(placementId) === atomKey) {
        updateMetricByKey(placementId, PlacementStatus.AdNoFill);
        updatePlacementKeyValueById(placementId, 'status', 'error');
        return;
      }

      lastProcessedAtom.set(placementId, atomKey);

      // Extract searchQuery and categories from searchParams
      const { searchQuery, categories } = extractRetailMediaParams(
        atom.searchParams
      );
      // safeguard to ensure we have the necessary data to trigger a request
      if (!categories && !searchQuery) {
        return;
      }
      triggerRequest(
        placementId,
        searchQuery,
        categories,
        this.displaySettings,
        atom.metadata?.brandNames
      );
    });

    // Mark this placement as initialized
    initializedPlacements.add(placementId);
  }

  override requestAd() {
    // is triggered by the config event listener, do nothing here
  }

  override render() {
    return html`
      <slot
        name="${AdSlot.fromPlacementId(this.placementId)}"
        id="${this.placementId}"
      ></slot>
    `;
  }
}
